import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ProductCard from './ProductCard';
import ProductFilter from './ProductFilter';
import ProductDetail from './ProductDetail';
import QuoteRequestForm from './QuoteRequestForm';

export default function ProductGrid() {
    const { t } = useTranslation();
    const config = window.astCatalog || {};
    const fixedCategory = config.productCategory || ''; // slug from [ast_products_<slug>]

    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [quoteTarget, setQuoteTarget] = useState(null); // array of products, or null

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        try {
            const apiUrl = config.apiUrl || '/wp-json/ast/v1';
            const headers = { 'X-WP-Nonce': config.nonce || '' };
            const params = { per_page: 200 };
            if (fixedCategory) params.product_category = fixedCategory;

            const prodRes = await axios.get(apiUrl + '/products', { headers, params });
            const prodList = Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data?.items || []);
            setProducts(prodList);

            // Derive the product-category filter chips from the loaded products.
            const catMap = new Map();
            prodList.forEach((p) => {
                if (p.product_category_id) {
                    catMap.set(String(p.product_category_id), {
                        id: Number(p.product_category_id),
                        name: p.product_category_name || '—',
                    });
                }
            });
            setCategories([...catMap.values()].sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            console.error('Failed to load catalog:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = useMemo(() => {
        let result = products;

        if (activeCategory) {
            result = result.filter((p) => Number(p.product_category_id) === Number(activeCategory));
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter((p) =>
                p.name.toLowerCase().includes(q) ||
                (p.sku && p.sku.toLowerCase().includes(q)) ||
                (p.description && p.description.toLowerCase().includes(q))
            );
        }

        return result;
    }, [products, activeCategory, searchQuery]);

    const toggleSelect = (product) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(product.id)) next.delete(product.id);
            else next.add(product.id);
            return next;
        });
    };

    const selectedProducts = useMemo(
        () => products.filter((p) => selectedIds.has(p.id)),
        [products, selectedIds]
    );

    if (loading) {
        return (
            <div className="ast-catalog">
                <div className="ast-loading">
                    <div className="ast-loading__spinner"></div>
                    <p>{t('app.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="ast-catalog">
            <h1 className="ast-catalog__title">{t('catalog.title')}</h1>
            {config.showPrices && (
                <p className="ast-catalog__price-note" style={{ margin: '0 0 12px', color: '#6b7280', fontSize: '13px' }}>
                    Alle prijzen zijn indicatief.
                </p>
            )}

            {/* Hide the category filter when the shortcode already fixes a category. */}
            {!fixedCategory && (
                <ProductFilter
                    categories={categories}
                    activeCategory={activeCategory}
                    searchQuery={searchQuery}
                    onCategoryChange={setActiveCategory}
                    onSearchChange={setSearchQuery}
                />
            )}

            {filteredProducts.length === 0 ? (
                <div className="ast-catalog__empty">
                    <p>{t('catalog.noResults')}</p>
                </div>
            ) : (
                <div className="ast-catalog__grid">
                    {filteredProducts.map((product) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            showPrices={config.showPrices}
                            selected={selectedIds.has(product.id)}
                            onToggleSelect={toggleSelect}
                            onSelect={setSelectedProduct}
                            onQuoteRequest={(p) => setQuoteTarget([p])}
                        />
                    ))}
                </div>
            )}

            {selectedProduct && (
                <ProductDetail
                    product={selectedProduct}
                    showPrices={config.showPrices}
                    selected={selectedIds.has(selectedProduct.id)}
                    onToggleSelect={toggleSelect}
                    onClose={() => setSelectedProduct(null)}
                    onQuoteRequest={(p) => { setSelectedProduct(null); setQuoteTarget([p]); }}
                />
            )}

            {quoteTarget && (
                <QuoteRequestForm
                    products={quoteTarget}
                    onClose={() => setQuoteTarget(null)}
                    onSuccess={() => setSelectedIds(new Set())}
                />
            )}

            {/* Sticky selection bar for multi-product quote requests. */}
            {selectedProducts.length > 0 && (
                <div className="ast-catalog__selection-bar" style={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
                    background: '#111827', color: '#fff', padding: '12px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
                    boxShadow: '0 -2px 12px rgba(0,0,0,.2)', flexWrap: 'wrap',
                }}>
                    <span>{selectedProducts.length} product{selectedProducts.length === 1 ? '' : 'en'} geselecteerd</span>
                    <button
                        className="ast-btn ast-btn--primary"
                        onClick={() => setQuoteTarget(selectedProducts)}
                    >
                        {t('catalog.requestQuote')} ({selectedProducts.length})
                    </button>
                    <button
                        className="ast-btn"
                        style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.4)' }}
                        onClick={() => setSelectedIds(new Set())}
                    >
                        Wis selectie
                    </button>
                </div>
            )}
        </div>
    );
}
